// components/TrialStatus.js
import React, { useState, useEffect } from 'react';
import FreeTrialManager from './FreeTrialManager';
import './TrialStatus.css';

const TrialStatus = () => {
  const [isActive, setIsActive] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [isLowResources, setIsLowResources] = useState(false);
  const [total] = useState(15); // Store total for percentage calculation
  
  useEffect(() => {
    // Check if final prompt was already used (key added in FreeTrialManager)
    const isFinalPromptUsed = () => {
      return localStorage.getItem('freeTrialFinalPromptUsed') === 'true';
    };
  
    // Initial update
    const updateStatus = () => {
      try {
        const storedTrial = localStorage.getItem('freeTrialData');
        if (storedTrial) {
          const parsedData = JSON.parse(storedTrial);
          
          // Determine active status with final prompt check
          const finalPromptUsed = isFinalPromptUsed();
          
          // If the final prompt was used but we still show 1 remaining, override to inactive
          let isReallyActive = parsedData.active && parsedData.remaining > 0;
          if (finalPromptUsed && parsedData.remaining === 1) {
            console.log("TrialStatus: Final prompt was previously used, showing as inactive");
            isReallyActive = false;
            // Don't modify the localStorage here, let FreeTrialManager handle that
          }
          
          setIsActive(isReallyActive);
          
          // Show the true remaining value (0 if final prompt used but UI shows 1)
          let actualRemaining = parsedData.remaining;
          if (finalPromptUsed && parsedData.remaining === 1) {
            console.log("TrialStatus: Final prompt was used, showing 0 remaining");
            actualRemaining = 0;
          }
          setRemaining(actualRemaining);
          
          // Check if resources are running low (20% or less)
          // This matches the WARNING_THRESHOLD in FreeTrialManager
          const INITIAL_PROMPT_COUNT = 15;
          const WARNING_THRESHOLD = 0.2;
          const remainingPercentage = actualRemaining / INITIAL_PROMPT_COUNT;
          
          if (isReallyActive && remainingPercentage <= WARNING_THRESHOLD) {
            setIsLowResources(true);
          } else {
            setIsLowResources(false);
          }
        }
      } catch (err) {
        console.error("TrialStatus: Error reading localStorage:", err);
      }
    };
    
    updateStatus();
    
    // Listen for the trial updated event
    const handleTrialUpdate = () => {
      console.log("TrialStatus: Received trialUpdated event");
      updateStatus();
    };
    
    window.addEventListener('trialUpdated', handleTrialUpdate);
    
    return () => {
      window.removeEventListener('trialUpdated', handleTrialUpdate);
    };
  }, []);
  
  if (!isActive) {
    return null;
  }
  
  // Calculate percentage for more precise display
  const percentRemaining = Math.round((remaining / total) * 100);
  
  // Use a very specific style that will definitely show up
  return (
    <div style={{
      position: 'fixed',
      top: '10px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 10000,
      backgroundColor: isLowResources ? '#fff1f0' : '#e6f7ff',
      color: isLowResources ? '#cf1322' : '#0050b3',
      padding: '12px 20px',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      border: isLowResources ? '2px solid #ff4d4f' : '2px solid #1890ff',
      fontWeight: 'bold',
      fontSize: '16px',
      textAlign: 'center',
      width: 'auto',
      maxWidth: '90%',
      margin: '0 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.3s ease'
    }}>
      <span style={{ 
        marginRight: '10px', 
        fontSize: '20px' 
      }}>
        {isLowResources ? '⚠️' : '🎁'}
      </span>
      <span>
        <strong>Free Trial:</strong> {remaining} {remaining === 1 ? 'prompt' : 'prompts'} remaining
        {isLowResources && (
          <span style={{ 
            color: '#ff4d4f',
            marginLeft: '8px', 
            fontWeight: 'bold',
            animation: 'pulse 2s infinite'
          }}>
            ({percentRemaining}% remaining)
          </span>
        )}
      </span>
      
      <style jsx>{`
        @keyframes pulse {
          0% { opacity: 0.7; }
          50% { opacity: 1; }
          100% { opacity: 0.7; }
        }
      `}</style>
    </div>
  );
};

export default TrialStatus;